import { isRecord } from '../util/type-guards.ts';
import {
  readVisitorContextUpdate,
  type VisitorContextUpdate,
} from '../../../../../shared/visitor-context.ts';

/** Non-UI tools whose result must reach a store instead of rendering. */
export const VOICE_TOOL_NAME = 'playVoiceAssistance';
export const MEMORY_BANK_TOOL_NAME = 'persistToMemoryBank';

export type SignalToolUpdate =
  | { kind: 'voice'; text: string }
  | { kind: 'memory'; update: VisitorContextUpdate };

/** Content channel a signal tool's result can arrive on. */
export type SignalToolChannel = 'tool' | 'component';

interface SignalToolRegistryEntry {
  readonly name: string;
  /** The content channel this tool's result is EXPECTED to arrive on. */
  readonly channel: SignalToolChannel;
  readonly kind: SignalToolUpdate['kind'];
}

/**
 * Signal tools without a component name normally arrive on the Tool channel.
 * The registry also allows detection of accidental channel changes.
 */
const SIGNAL_TOOL_REGISTRY: readonly SignalToolRegistryEntry[] = [
  { name: VOICE_TOOL_NAME, channel: 'tool', kind: 'voice' },
  { name: MEMORY_BANK_TOOL_NAME, channel: 'tool', kind: 'memory' },
];

function findSignalToolEntry(toolName: string | undefined): SignalToolRegistryEntry | undefined {
  return SIGNAL_TOOL_REGISTRY.find((entry) => entry.name === toolName);
}

/**
 * Validates signal payloads on either supported channel. Unexpected routing
 * is reported without logging personal content; valid updates still apply.
 */
export function resolveSignalToolUpdate(
  toolName: string | undefined,
  payload: unknown,
  arrivedOn: SignalToolChannel,
): SignalToolUpdate | null {
  const entry = findSignalToolEntry(toolName);
  if (!entry) {
    return null;
  }
  if (entry.channel !== arrivedOn) {
    console.warn(
      `[signal-tool] ${entry.name} arrived as ${channelLabel(arrivedOn)} but is registered as ${entry.channel}-channel — handler may be misrouted (see signal-tool-payload.ts)`,
    );
  }
  if (entry.kind === 'memory') {
    const update = readVisitorContextUpdate(payload);
    return update ? { kind: 'memory', update } : null;
  }
  const value = stringField(payload, 'text');
  if (!value) {
    return null;
  }
  return { kind: 'voice', text: value };
}

function channelLabel(channel: SignalToolChannel): string {
  return channel === 'component' ? 'Component' : 'Tool';
}

function stringField(payload: unknown, key: string): string | null {
  if (!isRecord(payload)) {
    return null;
  }
  const value = payload[key];
  return typeof value === 'string' ? value : null;
}
